import React, { useState, useEffect } from 'react';
import { getSkills as fetchSkills, adminCreateSkill, adminUpdateSkill, adminDeleteSkill } from '../../api/client';
import { Plus, Trash2, Save, Loader2, Edit3 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const cats = [
  { key: 'hardware', label: 'Hardware', color: '#f59e0b' },
  { key: 'software', label: 'Software', color: '#00e5ff' },
  { key: 'networking', label: 'Networking', color: '#22d3ee' },
];

const inputStyle = { background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', color: '#f2f2ff', fontFamily: "'DM Sans', sans-serif", fontSize: 14, borderRadius: 3 };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: '#6a6a88', marginBottom: 6, display: 'block' };

function SkillRow({ skill, onUpdate, onDelete, color }) {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(skill);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await adminUpdateSkill(skill.id, { name: data.name, level: Number(data.level), description: data.description, category: skill.category, sort_order: Number(data.order || 0) }); onUpdate(); toast.success('Keahlian diperbarui!'); setEditing(false); }
    catch (e) { toast.error('Gagal: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Hapus keahlian ini?')) return;
    try { await adminDeleteSkill(skill.id); onUpdate(); toast.success('Keahlian dihapus.'); }
    catch (e) { toast.error('Gagal: ' + e.message); }
  };

  return (
    <div style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${editing ? color + '40' : 'rgba(0,229,255,0.06)'}`, borderRadius: 3, padding: 16, marginBottom: 10, transition: 'border-color 0.2s' }}>
      {editing ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div><Label style={labelStyle}>Nama</Label><Input value={data.name} onChange={e => setData(p => ({ ...p, name: e.target.value }))} style={inputStyle} /></div>
            <div><Label style={labelStyle}>Level (%)</Label><Input type='number' min='0' max='100' value={data.level} onChange={e => setData(p => ({ ...p, level: e.target.value }))} style={inputStyle} /></div>
            <div><Label style={labelStyle}>Urutan</Label><Input type='number' value={data.order || 0} onChange={e => setData(p => ({ ...p, order: e.target.value }))} style={inputStyle} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><Label style={labelStyle}>Deskripsi Singkat</Label><Input value={data.description} onChange={e => setData(p => ({ ...p, description: e.target.value }))} style={inputStyle} /></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: `linear-gradient(135deg, ${color}, ${color}cc)`, color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer' }}>{saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={13} />} Simpan</button>
            <button onClick={() => setEditing(false)} style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#6a6a88', cursor: 'pointer', fontSize: 13 }}>Batal</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#f2f2ff' }}>{skill.name}</span>
              <span style={{ fontSize: 12, color, fontFamily: "'DM Mono', monospace", background: `${color}15`, padding: '2px 8px', borderRadius: 30 }}>{skill.level}%</span>
            </div>
            <div style={{ height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 30, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${skill.level}%`, background: `linear-gradient(90deg, ${color}70, ${color})`, borderRadius: 30 }} />
            </div>
            {skill.description && <div style={{ fontSize: 12, color: '#6a6a88', marginTop: 4 }}>{skill.description}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setEditing(true)} style={{ padding: '6px 10px', borderRadius: 7, background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#00e5ff', cursor: 'pointer' }}><Edit3 size={13} /></button>
            <button onClick={handleDelete} style={{ padding: '6px 10px', borderRadius: 7, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SkillsManager() {
  const [skills, setSkills] = useState({ hardware: [], software: [], networking: [] });
  const [loading, setLoading] = useState(true);
  const [newSkill, setNewSkill] = useState({ name: '', level: 80, description: '', category: 'hardware' });
  const [adding, setAdding] = useState(false);

  const load = async () => { setLoading(true); try { setSkills(await fetchSkills()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const handleAdd = async (cat) => {
    if (!newSkill.name) { toast.error('Nama keahlian wajib diisi.'); return; }
    setAdding(true);
    try {
      await adminCreateSkill({ name: newSkill.name, level: Number(newSkill.level), description: newSkill.description, category: cat, sort_order: (skills[cat]?.length || 0) + 1 });
      setNewSkill({ name: '', level: 80, description: '', category: cat });
      load(); toast.success('Keahlian ditambahkan!');
    } catch (e) { toast.error(e.message); }
    finally { setAdding(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontWeight: 700, fontSize: 22, color: '#f2f2ff', margin: '0 0 4px', letterSpacing: '-0.3px' }}>Manajemen Keahlian</h1>
        <p style={{ fontSize: 13, color: '#6a6a88', margin: 0 }}>Tambah, edit, dan hapus skills per kategori</p>
      </div>

      {loading ? (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#6a6a88', gap: 10 }}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />Memuat...</div>) : (
        <Tabs defaultValue='hardware'>
          <TabsList style={{ background: 'var(--card)', border: '1px solid rgba(255,255,255,0.07)', padding: 4, borderRadius: 3, marginBottom: 24, display: 'inline-flex', gap: 2 }}>
            {cats.map(c => <TabsTrigger key={c.key} value={c.key} style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, borderRadius: 9, padding: '8px 18px', color: '#6a6a88' }}>{c.label} ({skills[c.key]?.length || 0})</TabsTrigger>)}
          </TabsList>

          {cats.map(cat => (
            <TabsContent key={cat.key} value={cat.key}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
                <div>
                  <h3 style={{ fontWeight: 700, fontSize: 15, color: '#f2f2ff', marginBottom: 16 }}>Keahlian {cat.label}</h3>
                  {(skills[cat.key] || []).map(skill => <SkillRow key={skill.id} skill={skill} onUpdate={load} color={cat.color} />)}
                  {(skills[cat.key] || []).length === 0 && <div style={{ fontSize: 13, color: '#6a6a88', padding: 20, textAlign: 'center', border: '1px dashed rgba(168,85,247,0.2)', borderRadius: 3 }}>Belum ada keahlian di kategori ini.</div>}
                </div>
                <div>
                  <div style={{ background: 'var(--card)', border: `1px dashed ${cat.color}40`, borderRadius: 4, padding: 20, position: 'sticky', top: 20 }}>
                    <h4 style={{ fontWeight: 700, fontSize: 14, color: cat.color, marginBottom: 16 }}>+ Tambah Keahlian {cat.label}</h4>
                    <Label style={labelStyle}>Nama</Label>
                    <Input value={newSkill.name} onChange={e => setNewSkill(p => ({ ...p, name: e.target.value }))} placeholder='e.g. Perakitan PC' style={{ ...inputStyle, marginBottom: 12 }} />
                    <Label style={labelStyle}>Level (%)</Label>
                    <Input type='number' min='0' max='100' value={newSkill.level} onChange={e => setNewSkill(p => ({ ...p, level: e.target.value }))} style={{ ...inputStyle, marginBottom: 12 }} />
                    <Label style={labelStyle}>Deskripsi</Label>
                    <Input value={newSkill.description} onChange={e => setNewSkill(p => ({ ...p, description: e.target.value }))} placeholder='Deskripsi singkat...' style={{ ...inputStyle, marginBottom: 16 }} />
                    <button onClick={() => handleAdd(cat.key)} disabled={adding} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 9, background: `linear-gradient(135deg, ${cat.color}, ${cat.color}cc)`, color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.7 : 1 }}>
                      {adding ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />} Tambah
                    </button>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
